import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatIcon } from "@angular/material/icon";
import { BscCatalogos, BscCiclo, BscRes, CorteBsc } from "src/app/shared/model/bsc/bsc.model";
import { BscService } from "../bsc.service";

/** Lo que avisa el selector cada vez que cambia (y una vez al cargar). */
export interface SeleccionCorteBsc {
  anio: number;
  /** Nulo cuando el selector se usa sin corte (T4). */
  corte: CorteBsc | null;
}

/** Una opción del combo de años. */
interface OpcionAnio {
  anio: number;
  texto: string;
}

const CORTES: { valor: CorteBsc; texto: string }[] = [
  { valor: "1", texto: "Q1" },
  { valor: "2", texto: "Q2" },
  { valor: "3", texto: "Q3" },
  { valor: "4", texto: "Q4" },
  { valor: "ANUAL", texto: "Anual" },
];

/**
 * BSC · barra de Año + Corte (Q1..Q4 / Anual) (docs/BSC.md §7).
 *
 * Se alimenta de catalogos.do: los años son los ciclos de la empresa con su
 * estatus, más el año en curso aunque aún no tenga ciclo; con
 * `incluirSiguiente` agrega el año siguiente (T4 lo abre para heredar y
 * preparar el ciclo). Sin `anio`/`corte` de entrada propone el año en curso
 * y el corte vigente que calcula el servidor.
 *
 * A diferencia de app-periodo-dashboard, la selección inicial la conoce
 * hasta que contesta el servidor, así que `cambio` se emite UNA vez al
 * cargar y después en cada cambio del usuario. `cargado` entrega los
 * catálogos para que la pantalla no los vuelva a pedir.
 *
 * Markup .tickets-toolbar de dashboard/shared/dashboard-tarjetas.css (no es
 * global: va en styleUrls, como app-periodo-dashboard). El host lleva
 * .tickets-dashboard para que tema-contenido.css pinte el select y los
 * rótulos en los tres temas. Lo que la pantalla quiera mostrar a la derecha
 * lo proyecta con un elemento marcado `meta`.
 */
@Component({
  selector: "app-selector-corte-bsc",
  standalone: true,
  templateUrl: "./selector-corte-bsc.component.html",
  styleUrls: ["../../dashboard/shared/dashboard-tarjetas.css", "./selector-corte-bsc.component.css"],
  imports: [CommonModule, FormsModule, MatIcon],
  host: { class: "tickets-dashboard selector-corte-bsc" },
})
export class SelectorCorteBscComponent implements OnInit {
  /** false = solo el año (T4). */
  @Input() mostrarCorte = true;
  /** Agrega el año siguiente al en curso. */
  @Input() incluirSiguiente = false;
  /** Selección inicial; si no viene, el año en curso. */
  @Input() anio: number | null = null;
  /** Selección inicial; si no viene, el corte vigente. */
  @Input() corte: CorteBsc | null = null;
  /** Prefijo de los id de los controles, por si hay dos barras en la página. */
  @Input() prefijo = "bsc";
  /** La pantalla bloquea el cambio mientras consulta o guarda. */
  @Input() deshabilitado = false;

  @Output() cambio = new EventEmitter<SeleccionCorteBsc>();
  @Output() cargado = new EventEmitter<BscRes>();

  readonly cortes = CORTES;
  anios: OpcionAnio[] = [];
  cargando = false;
  error = "";

  constructor(
    private readonly bscService: BscService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargar(true);
  }

  /**
   * Vuelve a pedir los catálogos sin mover la selección, p. ej. después de
   * guardar o cerrar un ciclo (cambia el estatus que se lee en el combo).
   */
  recargar(): void {
    this.cargar(false);
  }

  /**
   * Pone el año sin avisar `cambio`: para regresar el combo cuando la
   * pantalla no acepta el cambio (p. ej. hay cambios sin guardar).
   */
  fijarAnio(anio: number): void {
    if (!this.anios.some((a) => a.anio === anio)) {
      this.anios = [...this.anios, { anio, texto: `${anio}` }].sort((a, b) => b.anio - a.anio);
    }
    this.anio = anio;
    this.cdr.detectChanges();
  }

  onAnio(): void {
    this.emitir();
  }

  onCorte(): void {
    this.emitir();
  }

  private cargar(inicial: boolean): void {
    this.cargando = true;
    this.error = "";
    this.cdr.detectChanges();
    this.bscService.catalogos().subscribe({
      next: (res) => {
        this.cargando = false;
        if (res?.codigoError) {
          this.error = res.codigoError === "AUTH-001"
            ? "Tu sesión caducó. Vuelve a entrar."
            : res.mensajeError || "No se pudieron consultar los años del BSC.";
        }
        this.arma(res?.catalogos ?? {});
        if (!res?.codigoError) {
          this.cargado.emit(res);
        }
        if (inicial) {
          this.emitir();
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargando = false;
        this.error = "No se pudieron consultar los años del BSC: sin conexión con el servidor.";
        this.arma({});
        if (inicial) {
          this.emitir();
        }
        this.cdr.detectChanges();
      },
    });
  }

  /** Arma el combo y completa la selección que falte. */
  private arma(catalogos: BscCatalogos): void {
    const enCurso = catalogos.anioEnCurso ?? new Date().getFullYear();
    const porAnio = new Map<number, BscCiclo>();
    (catalogos.anios ?? []).forEach((c) => {
      if (c.anio != null) {
        porAnio.set(c.anio, c);
      }
    });
    const anios = new Set<number>(porAnio.keys());
    anios.add(enCurso);
    if (this.incluirSiguiente) {
      anios.add(enCurso + 1);
    }
    if (this.anio != null) {
      anios.add(this.anio);
    }
    this.anios = Array.from(anios)
      .sort((a, b) => b - a)
      .map((anio) => ({ anio, texto: this.textoAnio(anio, porAnio.get(anio)) }));

    this.anio ??= enCurso;
    if (this.mostrarCorte && this.corte == null) {
      this.corte = catalogos.corteVigente ?? "ANUAL";
    }
  }

  private textoAnio(anio: number, ciclo: BscCiclo | undefined): string {
    if (!ciclo) {
      return `${anio} · sin configurar`;
    }
    if (ciclo.estatus === "CERRADO") {
      return `${anio} · cerrado`;
    }
    if (ciclo.origen === "IMPORTACION_HISTORICA") {
      return `${anio} · histórico`;
    }
    return `${anio} · abierto`;
  }

  private emitir(): void {
    if (this.anio == null) {
      return;
    }
    this.cambio.emit({
      anio: Number(this.anio),
      corte: this.mostrarCorte ? this.corte : null,
    });
  }
}
